import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { UserProvider, useUser } from './contexts/UserContext';

import NeuralGlassHomepage from './components/NeuralGlassHomepage';
import NeuralHomepage from './components/NeuralHomepage';
import DesktopPage from './components/DesktopPage';
import InterviewAssistant from './components/InterviewPage';
import InterviewAssistantLanding from './components/InterviewAssistantLanding';
import CoverLetter from './components/CoverLetterPage';
import CoverLetterLanding from './components/CoverLetterLanding';
import CoverLetterStudioPage from './components/CoverLetterStudioPage';
import InterviewQuestions from './components/InterviewQuestionsPage';
import InterviewQuestionsLanding from './components/InterviewQuestionsLanding';
import BuildResume from './components/ResumeBuilderPage';
import ResumeBuilderLanding from './components/ResumeBuilderLanding';
import ResumeStudioPage from './components/ResumeStudioPage';
import ATSCheckLanding from './components/ATSCheckLanding';
import ATSStudioPage from './components/ATSStudioPage';
import Dashboard from './components/Dashboard';
import PaymentSuccess from './components/PaymentSuccess';
import MockInterviewPage from './components/MockInterviewPage';
import MockInterviewResults from './components/MockInterviewResults';
import MockInterviewLanding from './components/MockInterviewLanding';
import ProfilePage from './components/ProfilePage';
import LinkedInOptimizer from './components/LinkedInOptimizerPage';
import LinkedInOptimizerLanding from './components/LinkedInOptimizerLanding';
import LinkedInStudioPage from './components/LinkedInStudioPage';
import AutoJobApply from './components/AutoJobApply';
import AutoJobApplyLanding from './components/AutoJobApplyLanding';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import About from './components/About';
import FloatingOverlay from './components/FloatingOverlay';
import CertificationPage from './components/CertificationPage';
import CertificationLanding from './components/CertificationLanding';
import PortfolioPage from './components/PortfolioPage';
import PortfolioBuilderLanding from './components/PortfolioBuilderLanding';
import PublicPortfolioPage from './components/PublicPortfolioPage';
import BlogIndex from './components/BlogIndex';
import BlogPost from './components/BlogPost';
import BlogEditor from './components/BlogEditor';
import Onboarding from './components/Onboarding/Onboarding';
import { ToastProvider } from './components/Toast/ToastProvider';
import ElectronShell from './electron/ElectronShell';
import TeraChat from './components/TeraChat';

import './App.css';
/* Theme tokens + the shared light-mode override layer. Imported here rather
   than from individual pages so every route carries them — the studios used
   to pull them in privately, which meant a page that forgot the import had
   no light styling at all. */
import './components/studioTheme.css';
import './components/neuralPageOverrides.theme.css';
import './components/landingTheme.css';
import { useStudioTheme } from './hooks/useStudioTheme';
import StudioThemeSwitch from './components/StudioThemeSwitch';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Gates the app on first run. If a logged-in user has no profile yet (or
// is_complete = 0), redirects them to /onboarding the moment they try to
// touch any feature page. Public pages and /onboarding itself are exempt.
const PUBLIC_PATHS = new Set([
  '/', '/privacy-policy', '/terms-of-service', '/about',
  '/floating', '/payment-success', '/onboarding',
]);
const PUBLIC_PREFIXES = ['/p/']; // public portfolio
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const OnboardingGate = () => {
  const { user, loading } = useUser();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !user) return;
    if (PUBLIC_PATHS.has(pathname) || PUBLIC_PREFIXES.some(p => pathname.startsWith(p))) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/profile/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        // The /profile/status endpoint returns { profileComplete }, NOT
        // { isComplete }. Reading the wrong field name caused an infinite
        // redirect loop where a finished user kept getting bounced back here.
        if (!data.profileComplete && pathname !== '/onboarding') {
          navigate('/onboarding', { replace: true });
        }
      } catch { /* network errors → don't block; fail open */ }
    })();
    return () => { cancelled = true; };
  }, [user, loading, pathname, navigate]);

  // Pure route-level side-effect guard. Renders nothing.
  return null;
};

const isElectron = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isElectronUA = userAgent.includes('electron');
  const hasProcess = window.process && window.process.type;
  return isElectronUA || !!hasProcess;
};

const RouterComponent = isElectron() ? HashRouter : BrowserRouter;
const IS_ELECTRON = isElectron();

// Routes where the floating Tera chat would compete with the workspace
// or modal (live interview, mock-interview session, billing checkout, etc.)
// Add more here as needed — match against the start of the pathname.
const TERA_HIDDEN_PREFIXES = [
  '/interview-session',
  '/mock-interview-session',
  '/auto-apply-session',
  // Studios: full-height two-column workspaces. The floating bubble sits on
  // top of the right-hand card's action row (downloads / template picker).
  '/cover-letter-session',
  '/ats-studio',
  '/resume-studio',
  '/linkedin-studio',
];

// Hides the floating Tera chat on routes where it would crowd the UI.
const TeraChatGate = () => {
  const { pathname } = useLocation();
  const hidden = TERA_HIDDEN_PREFIXES.some(p => pathname.startsWith(p));
  if (hidden) return null;
  return <TeraChat />;
};

// All app routes in one place so we can render them inside ElectronShell
// (desktop) or directly under <Routes> (web) without duplicating the list.
const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<NeuralHomepage />} />
    <Route path="/home-legacy" element={<NeuralGlassHomepage />} />
    <Route path="/onboarding" element={<Onboarding />} />
    <Route path="/profile" element={<ProfilePage />} />
    <Route path="/InterviewAssistant" element={<InterviewAssistantLanding />} />
    <Route path="/desktop" element={<DesktopPage />} />
    <Route path="/interview-session" element={<InterviewAssistant embedded={IS_ELECTRON} />} />
    <Route path="/BuildResume" element={<Navigate to="/resume-builder" replace />} />
    <Route path="/resume-builder" element={<ResumeBuilderLanding />} />
    <Route path="/resume-studio" element={<ResumeStudioPage />} />
    <Route path="/resume-builder-legacy" element={<BuildResume />} />
    <Route path="/CoverLetter" element={<CoverLetterLanding />} />
    <Route path="/cover-letter-session" element={<CoverLetterStudioPage />} />
    <Route path="/cover-letter-legacy" element={<CoverLetter />} />
    <Route path="/InterviewQuestions" element={<InterviewQuestionsLanding />} />
    <Route path="/interview-questions-session" element={<InterviewQuestions />} />
    <Route path="/ATSCheck" element={<ATSCheckLanding />} />
    <Route path="/ats-studio" element={<ATSStudioPage />} />
    {/* Old inline-tool route. The scanner now lives in its own studio, so
        keep this pointing there rather than shipping a second implementation. */}
    <Route path="/ats-check-session" element={<Navigate to="/ats-studio" replace />} />
    <Route path="/Dashboard" element={<Dashboard />} />
    <Route path="/payment-success" element={<PaymentSuccess />} />
    <Route path="/mockInterview" element={<MockInterviewLanding />} />
    <Route path="/mock-interview-session" element={<MockInterviewPage />} />
    <Route path="/mock-interview-results" element={<MockInterviewResults />} />
    <Route path="/LinkedInOptimizer" element={<LinkedInOptimizerLanding />} />
    <Route path="/linkedin-studio" element={<LinkedInStudioPage />} />
    <Route path="/linkedin-optimizer-session" element={<LinkedInOptimizer />} />
    <Route path="/AutoJobApply" element={<AutoJobApplyLanding />} />
    <Route path="/auto-apply-session" element={<AutoJobApply />} />
    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
    <Route path="/terms-of-service" element={<TermsOfService />} />
    <Route path="/about" element={<About />} />
    <Route path="/floating" element={<FloatingOverlay />} />
    <Route path="/certification" element={<CertificationLanding />} />
    <Route path="/certification-session" element={<CertificationPage />} />
    <Route path="/portfolio" element={<PortfolioBuilderLanding />} />
    <Route path="/portfolio-session" element={<PortfolioPage />} />
    <Route path="/p/:slug" element={<PublicPortfolioPage />} />
    <Route path="/blog" element={<BlogIndex />} />
    <Route path="/blog/admin/new" element={<BlogEditor mode="new" />} />
    <Route path="/blog/admin/edit/:id" element={<BlogEditor mode="edit" />} />
    <Route path="/blog/:slug" element={<BlogPost />} />
  </Routes>
);

/* The themed shell. Split out of <App> because useStudioTheme reads
   UserContext, so it has to run inside <UserProvider>.

   Mounting the theme here rather than page by page is deliberate: the class
   lands on `.App` and on <body>, so every route — including the ~25 pages
   that never opted in — resolves the shared `.cls-light` rules. Disabled
   under Electron, whose panes are dark by design and have their own chrome. */
const AppShell = () => {
  const themeClass = useStudioTheme(!IS_ELECTRON);
  return (
        <div className={`App ${themeClass}`}>
          {/* One theme toggle for the whole app, docked to the same spot on
              every route. It used to be mounted per page, which meant the
              ~25 pages that never opted in had no way to switch, and the
              nine that did each had to remember the dock markup. */}
          {!IS_ELECTRON && (
            <div className="cls-theme-dock">
              <StudioThemeSwitch />
            </div>
          )}
          {IS_ELECTRON ? (
            // Desktop layout — sidebar + active pane. ElectronShell decides
            // when to render the routed page full-bleed (live sessions) vs
            // its own pane content (feature task panes).
            <ElectronShell>
              <AppRoutes />
            </ElectronShell>
          ) : (
            <>
              <AppRoutes />
              {/* Floating AI chat agent — web only. Hidden inside the
                  Electron shell which has its own first-class panes,
                  AND on live interview / mock interview sessions where
                  the chat would crowd the workspace. */}
              <TeraChatGate />
            </>
          )}
        </div>
  );
};

function App() {
  return (
    <UserProvider>
      <ToastProvider>
        <RouterComponent>
          <ScrollToTop />
          <OnboardingGate />
          <AppShell />
        </RouterComponent>
      </ToastProvider>
    </UserProvider>
  );
}

export default App;
